import React, { useState, useEffect } from 'react';
import { Hypothesis, Problem } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

// Safe parsing function for hypothesis statements
interface ParseResult {
  success: boolean;
  aiFeature: string;
  businessResults: string[];
  stakeholders: string[];
}

const safeParseHypothesisStatement = (statement: string): ParseResult => {
  if (!statement || typeof statement !== 'string') {
    return { success: false, aiFeature: '', businessResults: [], stakeholders: [] };
  }
  
  // Clean the statement
  const cleanStatement = statement.trim();
  
  // Use case-insensitive regex to match the pattern
  const pattern = /we\s+believe\s+that\s+(.*?)\s+will\s+result\s+in\s+(.*?)\s+for\s+(.*?)(?:\.|$)/i;
  const match = cleanStatement.match(pattern);
  
  if (match && match[1] && match[2] && match[3]) {
    const aiFeaturePart = match[1].trim().replace(/^\[|\]$/g, '');
    const businessResultPart = match[2].trim();
    const stakeholderPart = match[3].trim();
    
    // Parse business results (handle multiple, remove brackets)
    const businessResultsList = businessResultPart
      .split(' and ')
      .map(result => result.trim().replace(/^\[|\]$/g, ''))
      .filter(result => result.length > 0);
    
    // Parse stakeholders (handle multiple, remove brackets)  
    const stakeholdersList = stakeholderPart
      .split(' and ')
      .map(stakeholder => stakeholder.trim().replace(/^\[|\]$/g, ''))
      .filter(stakeholder => stakeholder.length > 0);
    
    // Validate that all components are non-empty
    if (aiFeaturePart && businessResultsList.length > 0 && stakeholdersList.length > 0) {
      return {
        success: true,
        aiFeature: aiFeaturePart,
        businessResults: businessResultsList,
        stakeholders: stakeholdersList
      };
    }
  }
  
  // Fallback: try simpler parsing
  const statementLower = cleanStatement.toLowerCase();
  if (statementLower.includes('we believe that') && statementLower.includes('will result in') && statementLower.includes(' for ')) {
    try {
      const believePos = statementLower.indexOf('we believe that') + 'we believe that'.length;
      const resultPos = statementLower.indexOf('will result in');
      const forPos = statementLower.lastIndexOf(' for ');
      
      if (believePos < resultPos && resultPos < forPos) {
        const aiFeaturePart = cleanStatement.substring(believePos, resultPos).trim().replace(/^\[|\]$/g, '');
        const businessResultPart = cleanStatement.substring(resultPos + 'will result in'.length, forPos).trim();
        const stakeholderPart = cleanStatement.substring(forPos + ' for '.length).trim().replace(/\.$/, '');
        
        const businessResultsList = businessResultPart
          .split(' and ')
          .map(result => result.trim().replace(/^\[|\]$/g, ''))
          .filter(result => result.length > 0);
        
        const stakeholdersList = stakeholderPart
          .split(' and ')
          .map(stakeholder => stakeholder.trim().replace(/^\[|\]$/g, ''))
          .filter(stakeholder => stakeholder.length > 0);
        
        if (aiFeaturePart && businessResultsList.length > 0 && stakeholdersList.length > 0) {
          return {
            success: true,
            aiFeature: aiFeaturePart,
            businessResults: businessResultsList,
            stakeholders: stakeholdersList
          };
        }
      }
    } catch (error) {
      // Parsing failed, return unsuccessful result
    }
  }
  
  return { success: false, aiFeature: '', businessResults: [], stakeholders: [] };
};

interface HypothesisFormProps {
  hypothesis?: Hypothesis;
  problems: Problem[];
  preSelectedProblemId?: string | null;
  onSubmit: (hypothesis: Hypothesis) => void;
  onCancel: () => void;
  isEditing?: boolean;
}

const HypothesisForm: React.FC<HypothesisFormProps> = ({
  hypothesis,
  problems,
  preSelectedProblemId,
  onSubmit,
  onCancel,
  isEditing = false
}) => {
  const { canWrite } = useAuth();
  const navigate = useNavigate();

  const [formData, setFormData] = useState<Omit<Hypothesis, 'id' | 'priority_score'>>({
    title: '',
    hypothesis_statement: '',
    description: '',
    ai_capability: '',
    expected_outcome: '',
    testing_requirements: '',
    business_impact: 5,
    technical_feasibility: 5,
    implementation_effort: 5,
    strategy_alignment: 5,
    problem_id: '',
    project_id: '',
    metrics_to_track: []
  });

  const [metricInput, setMetricInput] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [metricSuggestions, setMetricSuggestions] = useState<string[]>([]);

  // Redirect viewers away from form
  useEffect(() => {
    if (!canWrite()) {
      navigate('/app-hypotheses');
    }
  }, [canWrite, navigate]);
  
  // Hypothesis statement components
  const [aiFeature, setAiFeature] = useState('');
  const [businessResults, setBusinessResults] = useState<string[]>([]);
  const [targetStakeholders, setTargetStakeholders] = useState<string[]>([]);
  
  // Input states for adding new items
  const [businessResultInput, setBusinessResultInput] = useState('');
  const [stakeholderInput, setStakeholderInput] = useState('');
  
  // Common suggestions from Streamlit app
  const commonAiFeatures = [
    "Natural language processing for customer support automation",
    "Predictive analytics for sales forecasting", 
    "Sentiment analysis for customer feedback processing",
    "Chatbot integration with knowledge base access",
    "AI assisted reporting/visualisation",
    "Image recognition",
    "Automated document classification and routing",
    "Event driven automation pipelines",
    "Voice recognition for hands-free operation"
  ];
  
  const commonBusinessResults = [
    "Reduction in customer support response time",
    "Increase in sales conversion rate",
    "Improvement in issue resolution time", 
    "Decrease in customer churn rate",
    "Increase in operational efficiency",
    "Reduction in manual processing time",
    "Improvement in data accuracy",
    "Increase in customer satisfaction scores",
    "Reduction in operational costs",
    "Improvement in decision-making speed",
    "Users can communicate results easily",
    "Higher customer satisfaction scores"
  ];
  
  const commonStakeholders = [
    "Customer (buyer)",
    "Customer (User)", 
    "Sales team",
    "Marketing department",
    "Customer support agents",
    "Product managers",
    "Operations team",
    "Finance department", 
    "Developers",
    "Executive leadership"
  ];

  useEffect(() => {
    if (hypothesis) {
      setFormData({
        title: hypothesis.title,
        hypothesis_statement: hypothesis.hypothesis_statement,
        description: hypothesis.description,
        ai_capability: hypothesis.ai_capability,
        expected_outcome: hypothesis.expected_outcome,
        testing_requirements: hypothesis.testing_requirements,
        business_impact: hypothesis.business_impact,
        technical_feasibility: hypothesis.technical_feasibility,
        implementation_effort: hypothesis.implementation_effort,
        strategy_alignment: hypothesis.strategy_alignment,
        problem_id: hypothesis.problem_id,
        project_id: hypothesis.project_id,
        metrics_to_track: hypothesis.metrics_to_track || []
      });
      
      // Parse existing hypothesis statement into components using safe parsing
      if (hypothesis.hypothesis_statement) {
        const stmt = hypothesis.hypothesis_statement;
        
        // Try to safely parse the statement
        const parseResult = safeParseHypothesisStatement(stmt);
        
        if (parseResult.success) {
          setAiFeature(parseResult.aiFeature);
          setBusinessResults(parseResult.businessResults);
          setTargetStakeholders(parseResult.stakeholders);
        }
        // If parsing fails, we preserve the original statement and leave components empty
        // This prevents the statement from being deleted during editing
      }
    }
  }, [hypothesis]);

  // Handle pre-selected problem and generate metric suggestions and stakeholder inheritance
  useEffect(() => {
    if (preSelectedProblemId && !hypothesis) {
      // Find the selected problem and extract its data
      const selectedProblem = problems.find(p => p.id === preSelectedProblemId);
      if (selectedProblem) {
        // Set the pre-selected problem and project_id
        setFormData(prev => ({ 
          ...prev, 
          problem_id: preSelectedProblemId,
          project_id: selectedProblem.project_id || ''
        }));
        
        // Extract metric suggestions
        if (selectedProblem.metrics) {
          const metricNames = selectedProblem.metrics.map(metric => metric.name);
          setMetricSuggestions(metricNames);
        }
        
        // Auto-populate stakeholders from the problem
        if (selectedProblem.stakeholders && selectedProblem.stakeholders.length > 0) {
          const problemStakeholders = selectedProblem.stakeholders.map(s => s.name);
          setTargetStakeholders(problemStakeholders);
        }
      }
    }
  }, [preSelectedProblemId, problems, hypothesis]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Generate hypothesis statement from components with formatted variables
      let generatedHypothesisStatement = '';
      if (aiFeature && businessResults.length > 0 && targetStakeholders.length > 0) {
        const businessResultsText = businessResults.length === 1 
          ? `[${businessResults[0]}]`
          : businessResults.map(result => `[${result}]`).join(' and ');
        
        const stakeholdersText = targetStakeholders.length === 1
          ? `[${targetStakeholders[0]}]`
          : targetStakeholders.map(stakeholder => `[${stakeholder}]`).join(' and ');
        
        generatedHypothesisStatement = `We believe that [${aiFeature}] will result in ${businessResultsText} for ${stakeholdersText}`;
      }
      
      // For editing: if we can't generate a new statement but have an original one, preserve it
      const finalHypothesisStatement = generatedHypothesisStatement || 
        (isEditing && hypothesis?.hypothesis_statement ? hypothesis.hypothesis_statement : '');
      
      // Validate that we have a hypothesis statement
      // For new hypotheses, require all components. For existing ones, preserve original statement.
      if (!finalHypothesisStatement.trim() && !isEditing) {
        alert('Please provide a hypothesis statement by filling in all components (AI feature, business results, and stakeholders).');
        setIsSubmitting(false);
        return;
      }

      // Calculate priority score based on the formula from the backend
      const priority_score = (
        (formData.business_impact + formData.technical_feasibility + formData.strategy_alignment) / 3
      ) - (formData.implementation_effort / 10);

      const hypothesisData = {
        ...formData,
        hypothesis_statement: finalHypothesisStatement,
        priority_score,
        ...(hypothesis?.id && { id: hypothesis.id }),  // Only include id if editing existing hypothesis
        ...(hypothesis?.is_active !== undefined && { is_active: hypothesis.is_active })  // Preserve is_active when editing
      } as Hypothesis;

      onSubmit(hypothesisData);
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const addMetric = () => {
    if (metricInput.trim()) {
      setFormData({
        ...formData,
        metrics_to_track: [...formData.metrics_to_track, metricInput.trim()]
      });
      setMetricInput('');
    }
  };

  const removeMetric = (index: number) => {
    setFormData({
      ...formData,
      metrics_to_track: formData.metrics_to_track.filter((_, i) => i !== index)
    });
  };

  // Business Result management
  const addBusinessResult = () => {
    if (businessResultInput.trim() && !businessResults.includes(businessResultInput.trim())) {
      setBusinessResults([...businessResults, businessResultInput.trim()]);
      setBusinessResultInput('');
    }
  };

  const removeBusinessResult = (index: number) => {
    setBusinessResults(businessResults.filter((_, i) => i !== index));
  };

  const addBusinessResultFromSuggestion = (result: string) => {
    if (!businessResults.includes(result)) {
      setBusinessResults([...businessResults, result]);
    }
  };

  // Stakeholder management
  const addStakeholder = () => {
    if (stakeholderInput.trim() && !targetStakeholders.includes(stakeholderInput.trim())) {
      setTargetStakeholders([...targetStakeholders, stakeholderInput.trim()]);
      setStakeholderInput('');
    }
  };

  const removeStakeholder = (index: number) => {
    setTargetStakeholders(targetStakeholders.filter((_, i) => i !== index));
  };

  const addStakeholderFromSuggestion = (stakeholder: string) => {
    if (!targetStakeholders.includes(stakeholder)) {
      setTargetStakeholders([...targetStakeholders, stakeholder]);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addMetric();
    }
  };

  return (
    <div className="bg-white shadow-sm rounded-lg p-6">
      <h2 className="text-lg font-medium text-gray-900 mb-6">
        {isEditing ? 'Edit Hypothesis' : 'Create New Hypothesis'}
      </h2>
      
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label htmlFor="title" className="block text-sm font-medium text-gray-700">
            Title *
          </label>
          <input
            type="text"
            id="title"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            required
          />
        </div>

        <div>
          <label htmlFor="problem_id" className="block text-sm font-medium text-gray-700">
            Related Problem *
          </label>
          <select
            id="problem_id"
            value={formData.problem_id}
            onChange={(e) => {
              const selectedProblem = problems.find(p => p.id === e.target.value);
              setFormData({ 
                ...formData, 
                problem_id: e.target.value,
                project_id: selectedProblem?.project_id || ''
              });
            }}
            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            required
          >
            <option value="">Select a problem</option>
            {problems.map((problem) => (
              <option key={problem.id} value={problem.id}>
                {problem.title}
              </option>
            ))}
          </select>
        </div>

        {/* Hypothesis Statement Builder */}
        <div className="space-y-4 border border-gray-200 rounded-lg p-4 bg-gray-50">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Hypothesis Statement *
            </label>
            <p className="text-sm text-gray-600 mb-4">
              Use the format: <strong>We believe that [AI feature] will result in [specific measurable business result] for [stakeholder/user]</strong>
            </p>
            
            {/* Show warning if editing and parsing failed */}
            {isEditing && hypothesis?.hypothesis_statement && 
             (!aiFeature || businessResults.length === 0 || targetStakeholders.length === 0) && (
              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-yellow-800">
                      Existing hypothesis statement preserved
                    </h3>
                    <div className="mt-1 text-sm text-yellow-700">
                      <p>
                        The original hypothesis statement could not be automatically parsed into components, 
                        but it has been preserved. You can still edit other fields and save your changes.
                      </p>
                      <div className="mt-2 p-2 bg-yellow-100 rounded text-xs font-mono">
                        "{hypothesis.hypothesis_statement}"
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* AI Feature */}
          <div>
            <label htmlFor="ai_feature" className="block text-sm font-medium text-gray-700">
              AI Feature {!isEditing && '*'}
            </label>
            <textarea
              id="ai_feature"
              rows={2}
              value={aiFeature}
              onChange={(e) => setAiFeature(e.target.value)}
              placeholder="Describe the AI capability or feature"
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              required={!isEditing}
            />
            <details className="mt-2">
              <summary className="text-sm text-indigo-600 cursor-pointer hover:text-indigo-700">
                Suggested AI Features (click to select)
              </summary>
              <div className="mt-2 space-y-1">
                {commonAiFeatures.map((feature, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => setAiFeature(feature)}
                    className="block w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded border"
                  >
                    {feature}
                  </button>
                ))}
              </div>
            </details>
          </div>

          {/* Business Results */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Specific Measurable Business Results
            </label>
            
            {/* Added Business Results */}
            {businessResults.length > 0 && (
              <div className="mb-3 space-y-2">
                <h4 className="text-sm font-medium text-gray-600">Added Results:</h4>
                {businessResults.map((result, index) => (
                  <div key={index} className="flex items-center justify-between p-2 bg-green-50 border border-green-200 rounded">
                    <span className="text-sm">{result}</span>
                    <button
                      type="button"
                      onClick={() => removeBusinessResult(index)}
                      className="text-red-600 hover:text-red-800 text-sm font-medium"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
            
            {/* Add New Business Result */}
            <div className="space-y-2">
              <input
                type="text"
                value={businessResultInput}
                onChange={(e) => setBusinessResultInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addBusinessResult())}
                placeholder="Add a quantifiable outcome (include numbers/percentages when possible)"
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              />
              <button
                type="button"
                onClick={addBusinessResult}
                disabled={!businessResultInput.trim()}
                className="px-3 py-1 text-sm text-indigo-600 hover:text-indigo-500 border border-indigo-300 rounded-md disabled:opacity-50"
              >
                + Add Business Result
              </button>
            </div>
            
            <details className="mt-2">
              <summary className="text-sm text-indigo-600 cursor-pointer hover:text-indigo-700">
                Suggested Business Results (click to add)
              </summary>
              <div className="mt-2 space-y-1">
                {commonBusinessResults
                  .filter(result => !businessResults.includes(result))
                  .map((result, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => addBusinessResultFromSuggestion(result)}
                      className="block w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded border"
                    >
                      + {result}
                    </button>
                  ))}
              </div>
            </details>
          </div>

          {/* Target Stakeholders */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Target Stakeholders/Users
            </label>
            
            {/* Inherited from Problem */}
            {preSelectedProblemId && targetStakeholders.length > 0 && (
              <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="text-sm font-medium text-blue-900 mb-2">
                  👥 Inherited from selected problem:
                </h4>
                <div className="text-xs text-blue-700">
                  Stakeholders from the problem have been automatically added. You can remove or add more below.
                </div>
              </div>
            )}
            
            {/* Added Stakeholders */}
            {targetStakeholders.length > 0 && (
              <div className="mb-3 space-y-2">
                <h4 className="text-sm font-medium text-gray-600">Added Stakeholders:</h4>
                {targetStakeholders.map((stakeholder, index) => (
                  <div key={index} className="flex items-center justify-between p-2 bg-blue-50 border border-blue-200 rounded">
                    <span className="text-sm">{stakeholder}</span>
                    <button
                      type="button"
                      onClick={() => removeStakeholder(index)}
                      className="text-red-600 hover:text-red-800 text-sm font-medium"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
            
            {/* Add New Stakeholder */}
            <div className="space-y-2">
              <input
                type="text"
                value={stakeholderInput}
                onChange={(e) => setStakeholderInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addStakeholder())}
                placeholder="Add who will benefit from this outcome"
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              />
              <button
                type="button"
                onClick={addStakeholder}
                disabled={!stakeholderInput.trim()}
                className="px-3 py-1 text-sm text-indigo-600 hover:text-indigo-500 border border-indigo-300 rounded-md disabled:opacity-50"
              >
                + Add Stakeholder
              </button>
            </div>
            
            <details className="mt-2">
              <summary className="text-sm text-indigo-600 cursor-pointer hover:text-indigo-700">
                Suggested Stakeholders (click to add)
              </summary>
              <div className="mt-2 grid grid-cols-2 gap-1">
                {commonStakeholders
                  .filter(stakeholder => !targetStakeholders.includes(stakeholder))
                  .map((stakeholder, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => addStakeholderFromSuggestion(stakeholder)}
                      className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded border text-left"
                    >
                      + {stakeholder}
                    </button>
                  ))}
              </div>
            </details>
          </div>

          {/* Generated Statement Preview */}
          <div className="border-t border-gray-300 pt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Generated Hypothesis Statement
            </label>
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              {aiFeature && businessResults.length > 0 && targetStakeholders.length > 0 ? (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-blue-900 italic">
                    "We believe that <span className="bg-yellow-100 px-1 rounded">[{aiFeature}]</span> will result in{' '}
                    {businessResults.length === 1 ? (
                      <span className="bg-yellow-100 px-1 rounded">[{businessResults[0]}]</span>
                    ) : (
                      businessResults.map((result, index) => (
                        <span key={index}>
                          <span className="bg-yellow-100 px-1 rounded">[{result}]</span>
                          {index < businessResults.length - 1 && ' and '}
                        </span>
                      ))
                    )}
                    {' '}for{' '}
                    {targetStakeholders.length === 1 ? (
                      <span className="bg-yellow-100 px-1 rounded">[{targetStakeholders[0]}]</span>
                    ) : (
                      targetStakeholders.map((stakeholder, index) => (
                        <span key={index}>
                          <span className="bg-yellow-100 px-1 rounded">[{stakeholder}]</span>
                          {index < targetStakeholders.length - 1 && ' and '}
                        </span>
                      ))
                    )}"
                  </p>
                  
                  {/* Summary */}
                  <div className="text-xs text-blue-700 pt-2 border-t border-blue-200">
                    <strong>Summary:</strong> {businessResults.length} business result{businessResults.length !== 1 ? 's' : ''} 
                    × {targetStakeholders.length} stakeholder{targetStakeholders.length !== 1 ? 's' : ''}
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <p className="text-sm text-gray-500 italic">
                    {isEditing 
                      ? "Fill in components above to generate a new hypothesis statement (optional when editing)"
                      : "Fill in all components above to see the generated hypothesis statement"
                    }
                  </p>
                  <div className="text-xs text-gray-400">
                    {isEditing 
                      ? "Optional: AI feature + at least 1 business result + at least 1 stakeholder"
                      : "Required: AI feature + at least 1 business result + at least 1 stakeholder"
                    }
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div>
          <label htmlFor="description" className="block text-sm font-medium text-gray-700">
            Description
          </label>
          <textarea
            id="description"
            rows={4}
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Detailed description of the hypothesis and its context (optional)"
            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          />
        </div>

        <div>
          <label htmlFor="ai_capability" className="block text-sm font-medium text-gray-700">
            AI Capability
          </label>
          <textarea
            id="ai_capability"
            rows={3}
            value={formData.ai_capability}
            onChange={(e) => setFormData({ ...formData, ai_capability: e.target.value })}
            placeholder="Describe the AI capability or technology that will be used (optional)"
            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          />
        </div>

        <div>
          <label htmlFor="expected_outcome" className="block text-sm font-medium text-gray-700">
            Expected Business Outcome
          </label>
          <textarea
            id="expected_outcome"
            rows={3}
            value={formData.expected_outcome}
            onChange={(e) => setFormData({ ...formData, expected_outcome: e.target.value })}
            placeholder="Describe the expected business impact and outcomes (optional)"
            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          />
        </div>

        <div>
          <label htmlFor="testing_requirements" className="block text-sm font-medium text-gray-700">
            Testing Requirements
          </label>
          <textarea
            id="testing_requirements"
            rows={3}
            value={formData.testing_requirements}
            onChange={(e) => setFormData({ ...formData, testing_requirements: e.target.value })}
            placeholder="Describe how this hypothesis will be tested and validated (optional)"
            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          />
        </div>

        {/* Scoring Section */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="text-sm font-medium text-gray-900 mb-4">Scoring (1-10 scale)</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="business_impact" className="block text-xs font-medium text-gray-700">
                Business Impact ({formData.business_impact}/10)
              </label>
              <input
                type="range"
                id="business_impact"
                min="1"
                max="10"
                value={formData.business_impact}
                onChange={(e) => setFormData({ ...formData, business_impact: parseInt(e.target.value) })}
                className="mt-1 block w-full"
              />
              <div className="text-xs text-gray-500 mt-1">Higher = greater potential impact</div>
            </div>

            <div>
              <label htmlFor="technical_feasibility" className="block text-xs font-medium text-gray-700">
                Technical Feasibility ({formData.technical_feasibility}/10)
              </label>
              <input
                type="range"
                id="technical_feasibility"
                min="1"
                max="10"
                value={formData.technical_feasibility}
                onChange={(e) => setFormData({ ...formData, technical_feasibility: parseInt(e.target.value) })}
                className="mt-1 block w-full"
              />
              <div className="text-xs text-gray-500 mt-1">Higher = more technically feasible</div>
            </div>

            <div>
              <label htmlFor="strategy_alignment" className="block text-xs font-medium text-gray-700">
                Strategic Alignment ({formData.strategy_alignment}/10)
              </label>
              <input
                type="range"
                id="strategy_alignment"
                min="1"
                max="10"
                value={formData.strategy_alignment}
                onChange={(e) => setFormData({ ...formData, strategy_alignment: parseInt(e.target.value) })}
                className="mt-1 block w-full"
              />
              <div className="text-xs text-gray-500 mt-1">Higher = better aligned with strategy</div>
            </div>

            <div>
              <label htmlFor="implementation_effort" className="block text-xs font-medium text-gray-700">
                Implementation Effort ({formData.implementation_effort}/10)
              </label>
              <input
                type="range"
                id="implementation_effort"
                min="1"
                max="10"
                value={formData.implementation_effort}
                onChange={(e) => setFormData({ ...formData, implementation_effort: parseInt(e.target.value) })}
                className="mt-1 block w-full"
              />
              <div className="text-xs text-gray-500 mt-1">Higher = more effort required</div>
            </div>
          </div>
        </div>

        {/* Metrics Section */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Metrics to Track
          </label>
          <div className="space-y-2">
            {formData.metrics_to_track.map((metric, index) => (
              <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                <span className="text-sm">{metric}</span>
                <button
                  type="button"
                  onClick={() => removeMetric(index)}
                  className="text-red-600 hover:text-red-800 text-sm"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
          
          {/* Metric Suggestions from Selected Problem */}
          {metricSuggestions.length > 0 && (
            <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <h4 className="text-sm font-medium text-blue-900 mb-2">
                📊 Suggested metrics from the selected problem:
              </h4>
              <div className="flex flex-wrap gap-2">
                {metricSuggestions
                  .filter(suggestion => !formData.metrics_to_track.includes(suggestion))
                  .map((suggestion, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => setFormData(prev => ({
                        ...prev,
                        metrics_to_track: [...prev.metrics_to_track, suggestion]
                      }))}
                      className="inline-flex items-center px-3 py-1 text-xs font-medium text-blue-700 bg-blue-100 hover:bg-blue-200 border border-blue-300 rounded-full transition-colors"
                    >
                      + {suggestion}
                    </button>
                  ))}
              </div>
              {metricSuggestions.filter(suggestion => !formData.metrics_to_track.includes(suggestion)).length === 0 && (
                <p className="text-xs text-blue-600">All suggested metrics have been added!</p>
              )}
            </div>
          )}
          
          <div className="mt-2 flex gap-2">
            <input
              type="text"
              placeholder="Add a metric to track"
              value={metricInput}
              onChange={(e) => setMetricInput(e.target.value)}
              onKeyPress={handleKeyPress}
              className="flex-1 border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            />
            <button
              type="button"
              onClick={addMetric}
              className="px-3 py-2 text-sm text-indigo-600 hover:text-indigo-500 border border-indigo-300 rounded-md"
            >
              Add
            </button>
          </div>
        </div>

        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
          >
            {isSubmitting ? 'Saving...' : (isEditing ? 'Update Hypothesis' : 'Create Hypothesis')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default HypothesisForm;